import { Navigate, Outlet, createBrowserRouter } from "react-router";
import { ForgotPasswordPage, LoginPage, ProtectedRoute } from "./auth";
import { ExtractionPanel } from "./components/extraction-panel";
import { FilesPage } from "./pages/submission/files";
import { HeatmapPage } from "./pages/submission/heat-map";
import { InvoicePage } from "./pages/submission/invoice";
import { CreateInvoicePage } from "./pages/submission/invoice/create";
import { SubmissionLayout } from "./pages/submission/layout";
import { LossRunsPage } from "./pages/submission/loss-runs";
import { OperationalPage } from "./pages/submission/operational";
import { EndorsementsPage, PolicyDetailsPage, SubjectivitiesPage } from "./pages/submission/policy";
import { PremiumSelectionPage } from "./pages/submission/premium-selection";
import { RegulatoryPage } from "./pages/submission/regulatory";
import { SchedulePage } from "./pages/submission/schedule";
import { SUBMISSION_SECTIONS } from "./pages/submission/sections";
import { UwInformationPage } from "./pages/submission/uw-information";
import { WorkInProgressPage } from "./pages/submission/work-in-progress-page";
import { PoliciesListPage } from "./pages/policies-list";
import { PolicyDetailPage } from "./pages/policy-detail";
import { SubmissionsListPage } from "./pages/submissions-list";

const REAL_SECTION_IDS = new Set([
    "uw-information",
    "schedule",
    "loss-runs",
    "regulatory",
    "operational",
    "legal",
    "premium-selection",
    "policy-details",
    "endorsements",
    "subjectivities",
    "files",
    "invoice",
]);

/**
 * Sections that don't have a real page yet share the same WorkInProgress
 * body. We derive their routes from SUBMISSION_SECTIONS so the sidebar
 * nav, router, and save-and-continue flow stay in lock-step. Real pages
 * (uw-information, schedule, regulatory, legal — the latter powered by
 * the existing heatmap component) are wired explicitly below.
 */
const wipSectionRoutes = SUBMISSION_SECTIONS.filter((s) => !REAL_SECTION_IDS.has(s.id)).map((section) => ({
    path: section.path,
    element: <WorkInProgressPage sectionId={section.id} />,
}));

export const router = createBrowserRouter([
    {
        // Pathless root layout — renders the matched route via `<Outlet />`
        // plus the app-wide `ExtractionPanel`. The panel lives here (rather
        // than inside any page) so an in-flight upload survives navigation
        // between the list, the create flow, and every submission section,
        // and so its "View Submission" CTA can use router navigation.
        element: (
            <>
                <Outlet />
                <ExtractionPanel />
            </>
        ),
        children: [
            {
                path: "/",
                element: <Navigate to="/submissions" replace />,
            },
            {
                // Public auth routes — entry points that don't require a
                // session. Live outside the ProtectedRoute boundary.
                path: "/login",
                element: <LoginPage />,
            },
            {
                path: "/forgot-password",
                element: <ForgotPasswordPage />,
            },
            {
                path: "/submissions",
                element: (
                    <ProtectedRoute>
                        <SubmissionsListPage />
                    </ProtectedRoute>
                ),
            },
            {
                // Policies list (OR-1664): the book, one row per policy.
                path: "/policies",
                element: (
                    <ProtectedRoute>
                        <PoliciesListPage />
                    </ProtectedRoute>
                ),
            },
            {
                // Policy record (OR-1665 … OR-1668).
                path: "/policies/:policyId",
                element: (
                    <ProtectedRoute>
                        <PolicyDetailPage />
                    </ProtectedRoute>
                ),
            },
            {
                // Create flow — same SubmissionLayout, but the layout sees no `id`
                // param and bootstraps with a blank detail. Save creates the
                // submission and redirects to /submissions/:newId/uw-information.
                path: "/submissions/new",
                element: (
                    <ProtectedRoute>
                        <SubmissionLayout />
                    </ProtectedRoute>
                ),
                children: [{ index: true, element: <UwInformationPage /> }],
            },
            {
                // Create Invoice is full-screen: the Figma drops the section
                // nav and the submission header, so it sits beside
                // SubmissionLayout rather than inside it. Declared before
                // `/submissions/:id` so the static `invoice/new` segment wins.
                path: "/submissions/:id/invoice/new",
                element: (
                    <ProtectedRoute>
                        <CreateInvoicePage />
                    </ProtectedRoute>
                ),
            },
            {
                // Same screen, same fields — only the mode differs. The design
                // is explicit that View must not be a separate template.
                path: "/submissions/:id/invoice/:invoiceId/edit",
                element: (
                    <ProtectedRoute>
                        <CreateInvoicePage mode="edit" />
                    </ProtectedRoute>
                ),
            },
            {
                path: "/submissions/:id/invoice/:invoiceId",
                element: (
                    <ProtectedRoute>
                        <CreateInvoicePage mode="view" />
                    </ProtectedRoute>
                ),
            },
            {
                path: "/submissions/:id",
                element: (
                    <ProtectedRoute>
                        <SubmissionLayout />
                    </ProtectedRoute>
                ),
                children: [
                    { index: true, element: <Navigate to="uw-information" replace /> },
                    { path: "uw-information", element: <UwInformationPage /> },
                    { path: "schedule", element: <SchedulePage /> },
                    { path: "loss-runs", element: <LossRunsPage /> },
                    { path: "regulatory", element: <RegulatoryPage /> },
                    { path: "operational", element: <OperationalPage /> },
                    { path: "legal", element: <HeatmapPage /> },
                    { path: "calculations/premium-selection", element: <PremiumSelectionPage /> },
                    { path: "policy/details", element: <PolicyDetailsPage /> },
                    { path: "policy/endorsements", element: <EndorsementsPage /> },
                    { path: "policy/subjectivities", element: <SubjectivitiesPage /> },
                    { path: "files", element: <FilesPage /> },
                    { path: "invoice", element: <InvoicePage /> },
                    ...wipSectionRoutes,
                ],
            },
            {
                path: "*",
                element: <Navigate to="/submissions" replace />,
            },
        ],
    },
]);
